'use client';

import {
	IconBrandFacebook,
	IconBrandLinkedin,
	IconBrandTwitter,
	IconCheck,
	IconLink,
} from '@tabler/icons-react';
import { format } from 'date-fns';
import { AnimatePresence, motion } from 'framer-motion';
import Image from 'next/image';
import Link from 'next/link';
import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { strapiImage } from '@/lib/strapi/strapiImage';
import { cn } from '@/lib/utils';
import { Article } from '@/types/types';

interface BlogContentProps {
	article: Article;
}

interface Dictionary<T> {
	[key: string]: T;
}

// Element styling lives in globals.css (.blog-body); only legacy URLs are rewritten here.
const urlReplacements: Dictionary<string> = {
	'http://mtalkz.com': 'https://www.mtalkz.com',
	'https://wp.mtalkz.com': 'https://www.mtalkz.com',
	'https://mtalkz.com': 'https://www.mtalkz.com',
};

const AuthorCard = ({
	author,
	publishDate,
	isFooter = false,
}: {
	author: Article['author'];
	publishDate?: string;
	isFooter?: boolean;
}) => {
	if (!author) return null;

	return (
		<div
			className={cn(
				'flex items-center gap-4 rounded-xl p-5',
				isFooter ? 'mt-8 bg-neutral-50' : 'mb-8',
			)}>
			<div className="shrink-0">
				{author.profile_picture ? (
					<Image
						src={strapiImage(author.profile_picture.url ?? '')}
						alt={author.name}
						width={48}
						height={48}
						className="rounded-full object-cover"
					/>
				) : (
					<span className="flex h-12 w-12 items-center justify-center rounded-full bg-neutral-100 text-lg font-medium text-neutral-600">
						{author.name.charAt(0)}
					</span>
				)}
			</div>
			<div className="min-w-0 flex-1">
				<div className="mb-1 flex items-center gap-2">
					<p className="font-medium text-neutral-800">
						{author.name}
					</p>
					{publishDate && (
						<>
							<span className="text-neutral-300">•</span>
							<time
								dateTime={publishDate}
								className="text-sm text-neutral-500">
								{format(new Date(publishDate), 'MMMM dd, yyyy')}
							</time>
						</>
					)}
				</div>
				{author.bio && (
					<p className="line-clamp-2 text-sm text-neutral-600">
						{author.bio}
					</p>
				)}
			</div>
		</div>
	);
};

export function BlogContent({ article }: BlogContentProps) {
	const [shareUrls, setShareUrls] = useState<{
		twitter: string;
		facebook: string;
		linkedin: string;
	}>({
		twitter: '',
		facebook: '',
		linkedin: '',
	});

	const [copySuccess, setCopySuccess] = useState(false);

	useEffect(() => {
		const currentUrl = window.location.href;
		setShareUrls({
			twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(currentUrl)}&text=${encodeURIComponent(article.title)}`,
			facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(currentUrl)}`,
			linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(currentUrl)}`,
		});
	}, [article.title]);

	const handleCopyLink = async () => {
		try {
			await navigator.clipboard.writeText(window.location.href);
			setCopySuccess(true);
			toast.success('Link copied to clipboard!', {
				duration: 2000,
			});

			// Reset success state after animation
			setTimeout(() => {
				setCopySuccess(false);
			}, 2000);
		} catch (err) {
			toast.error('Failed to copy link');
			console.error('Failed to copy link:', err);
		}
	};

	const socialButtons = [
		{ icon: IconBrandTwitter, url: shareUrls.twitter },
		{ icon: IconBrandFacebook, url: shareUrls.facebook },
		{ icon: IconBrandLinkedin, url: shareUrls.linkedin },
		{
			icon: copySuccess ? IconCheck : IconLink,
			onClick: handleCopyLink,
			className: copySuccess ? 'text-green-600' : '',
		},
	];

	const ShareButton = ({
		item,
		size = 16,
	}: {
		item: (typeof socialButtons)[0];
		size?: number;
	}) => (
		<button
			onClick={item.onClick || (() => window.open(item.url, '_blank'))}
			className={cn(
				'rounded-full bg-neutral-50 p-1.5 transition-all duration-200 hover:bg-neutral-100',
				item.className || 'text-neutral-600',
				copySuccess && item.onClick ? 'bg-green-50' : '',
			)}>
			<AnimatePresence mode="wait">
				<motion.div
					key={copySuccess && item.onClick ? 'success' : 'default'}
					initial={{ scale: 0.5, opacity: 0 }}
					animate={{ scale: 1, opacity: 1 }}
					exit={{ scale: 0.5, opacity: 0 }}
					transition={{ duration: 0.2 }}>
					<item.icon size={size} />
				</motion.div>
			</AnimatePresence>
		</button>
	);

	const parseHtmlTags = (content: string) => {
		let html_content = content;
		let sample = content;
		for (const key in urlReplacements) {
			html_content = sample.replaceAll(key, urlReplacements[key]);
			sample = html_content;
		}
		// The page title is the only <h1>; demote any <h1> inside the article body
		return html_content
			.replace(/<h1(?=[\s>])/gi, '<h2')
			.replace(/<\/h1>/gi, '</h2>');
	};

	const article_content = article.content
		? parseHtmlTags(article.content)
		: '';
	// console.log(article.content);
	// const content = article.content.replaceAll(
	// 	'<a',
	// 	'<a class="text-blue-500"',
	// );

	// console.log(content);
	// console.log(article.content);
	return (
		<article className="blog-article-content mx-auto">
			<noscript>
				<style>{`
					.blog-article-content [style*='opacity'],
					.blog-article-content [style*='translateY'] {
						opacity: 1 !important;
						transform: none !important;
					}
				`}</style>
			</noscript>
			<motion.header
				initial={{ opacity: 0, y: 20 }}
				animate={{ opacity: 1, y: 0 }}
				className="mb-6">
				{article.image && article.image.url && (
					<div className="relative mb-4 aspect-[2/1] overflow-hidden rounded-xl">
						<Image
							src={strapiImage(article.image.url)}
							alt={article.title}
							fill
							className="object-cover"
							priority
						/>
					</div>
				)}

				<div className="space-y-4 text-center">
					{article.categories?.[0] && (
						<Link
							href={`/blog?category=${article.categories[0].slug}`}
							className="inline-block rounded-full bg-primary/10 px-3 py-1 text-sm font-medium text-primary transition-colors hover:bg-primary/20">
							{article.categories[0].name}
						</Link>
					)}

					<h1 className="text-2xl font-bold leading-tight text-neutral-800 sm:text-3xl lg:text-4xl">
						{article.title}
					</h1>

					{article.description && (
						<p className="mx-auto max-w-2xl text-base text-neutral-600 sm:text-lg">
							{article.description}
						</p>
					)}
				</div>
			</motion.header>

			{/* Author card at the top */}
			<AuthorCard
				author={article.author}
				publishDate={article.publishedAt}
			/>

			{/* Social sharing */}
			<div className="fixed left-4 top-1/3 hidden flex-col gap-2 lg:flex">
				{socialButtons.map((item, idx) => (
					<ShareButton key={idx} item={item} />
				))}
			</div>

			{/* Article content */}
			<motion.div
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 0.2 }}
				className="blog-body"
				// className="prose prose-base prose-h1:text-3xl prose-h1:font-bold prose-h1:mb-4 prose-h2:text-2xl prose-h2:font-bold prose-h2:mb-3 prose-h3:text-xl prose-h3:font-bold prose-h3:mb-3 prose-h4:text-lg prose-h4:font-bold prose-h4:mb-2 prose-h5:text-base prose-h5:font-bold prose-h5:mb-2 prose-h6:text-sm prose-h6:font-bold prose-h6:mb-2 prose-p:mb-4 prose-p:leading-relaxed prose-p:text-neutral-600 prose-a:text-blue-600 prose-a:no-underline hover:prose-a:text-blue-700 prose-blockquote:border-primary prose-blockquote:text-neutral-600 prose-code:rounded prose-code:bg-neutral-50 prose-code:text-neutral-700 prose-li:text-neutral-600 prose-img:rounded-lg mx-auto max-w-6xl"
				dangerouslySetInnerHTML={{ __html: article_content }}
			/>

			<footer className="mx-auto mt-8 max-w-3xl space-y-6 pt-6">
				{/* Author card at the bottom */}
				<AuthorCard author={article.author} isFooter />

				{article.tags?.length > 0 && (
					<div className="flex flex-wrap gap-2">
						{article.tags.map(tag => (
							<span
								key={tag.id}
								className="rounded-full bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-600">
								#{tag.name}
							</span>
						))}
					</div>
				)}

				{/* Mobile sharing */}
				<div className="flex justify-center gap-2 lg:hidden">
					{socialButtons.map((item, idx) => (
						<ShareButton key={idx} item={item} size={18} />
					))}
				</div>
			</footer>
		</article>
	);
}
